import { z } from "zod";
import { DateTimeWithOffset } from "@/lib/api/schemas/patterns";

/**
 * Files an API project hands its agent — the "Project knowledge" panel. The backend calls
 * them project files (`/project-files/`); only active ones travel with a batch to the worker.
 *
 * Hand-written against `ProjectFileOut` in the contract (2026-09-24). Left out on purpose:
 * `file_url` is a signed storage link with no CORS headers, so the browser cannot read it —
 * the preview goes through `download/` instead; `is_deleted` never shows up in a list.
 *
 * The contract marks four fields optional; the three `.default()`s below are its own
 * declared defaults, not ours. `file_size` has none, so a missing size stays unknown.
 */
export const ProjectFileType = z.enum(["openapi", "markdown", "postman", "env"]);
export type ProjectFileTypeValue = z.infer<typeof ProjectFileType>;

export const ProjectFile = z.object({
  id: z.number(),
  project_id: z.number(),
  /** What the panel shows — unique within the project (a duplicate is a 409). */
  name: z.string(),
  /** The name the file was uploaded under; `replace/` changes it, `name` stays. */
  filename: z.string().default(""),
  description: z.string().default(""),
  type: ProjectFileType,
  is_active: z.boolean().default(true),
  file_size: z
    .number()
    .nullish()
    .transform((size) => size ?? null),
  created_at: DateTimeWithOffset,
  updated_at: DateTimeWithOffset,
});
export type ProjectFileValue = z.infer<typeof ProjectFile>;
